'use client';

import { generateUUID } from '../utils/uuid';
import { MarketingCookies } from '../marketing/cookies-service';

/**
 * 🚀 ANALYTICS DATA COLLECTOR
 * Gathers ALL possible data for tracking purposes
 */

/**
 * Helper to get cookie value
 */
function getCookie(name: string): string {
  if (typeof document === 'undefined') return '';
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || '';
  return '';
}

/**
 * Get complete analytics context data
 */
export function getAnalyticsContext(salonId?: string, businessSlug?: string) {
  if (typeof window === 'undefined') {
    return {
      salonId,
      businessSlug,
      isServer: true,
    };
  }

  // 1. Browser & Device Info
  const userAgent = navigator.userAgent;
  const isMobile = /iPhone|iPad|iPod|Android/i.test(userAgent);
  const isTablet = /iPad|Android/i.test(userAgent) && !/Mobile/i.test(userAgent);
  
  // 2. Screen & Viewport
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  // 3. Connection Info
  const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
  const connectionType = (connection as any)?.effectiveType || 'unknown';
  const connectionDownlink = (connection as any)?.downlink || 0;
  
  // 4. Language & Locale
  const language = navigator.language || (navigator as any).userLanguage || 'unknown';
  const languages = navigator.languages?.join(',') || language;
  
  // 5. Platform & Timezone
  const platform = navigator.platform || 'unknown';
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
  // 6. Referrer & UTM
  const referrer = document.referrer || '';
  const urlParams = new URLSearchParams(window.location.search);
  const utmSource = urlParams.get('utm_source') || '';
  const utmMedium = urlParams.get('utm_medium') || '';
  const utmCampaign = urlParams.get('utm_campaign') || '';
  const utmTerm = urlParams.get('utm_term') || '';
  const utmContent = urlParams.get('utm_content') || '';
  
  // 7. Page & Session Info
  const currentUrl = window.location.href;
  const pagePath = window.location.pathname;
  const pageHash = window.location.hash || '';
  const sessionId = sessionStorage.getItem('sessionId') || (() => {
    const newId = generateUUID();
    sessionStorage.setItem('sessionId', newId);
    return newId;
  })();
  
  // 8. Session Storage Data
  const bookingStep = sessionStorage.getItem('booking_step') || 'none';
  const selectedServicesSession = sessionStorage.getItem('selected_services') || '';
  const bookingStartTime = sessionStorage.getItem('booking_start_time') || '';
  
  // 9. Local Storage Data (first visit, etc.)
  if (!localStorage.getItem('first_visit')) {
    localStorage.setItem('first_visit', new Date().toISOString());
  }
  const firstVisit = localStorage.getItem('first_visit') || new Date().toISOString();
  
  const currentVisitCount = parseInt(localStorage.getItem('visit_count') || '0', 10);
  const visitCount = currentVisitCount + 1;
  localStorage.setItem('visit_count', visitCount.toString());
  
  // 10. Performance Metrics
  const navigationStart = performance.timing?.navigationStart || 0;
  const pageLoadTime = Date.now() - navigationStart;
  
  // 11. Touch & Interaction capabilities
  const touchEnabled = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  const maxTouchPoints = navigator.maxTouchPoints || 0;
  
  // 12. Do Not Track
  const dnt = navigator.doNotTrack === '1' || (navigator as any).doNotTrack === 'yes';
  
  // 13. Cookie status
  const cookiesEnabled = navigator.cookieEnabled;
  
  // 14. WebGL & Graphics (for ad targeting)
  let webglRenderer = '';
  let webglVendor = '';
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null;
    if (gl) {
      webglRenderer = gl.getParameter(gl.RENDERER) || '';
      webglVendor = gl.getParameter(gl.VENDOR) || '';
    }
  } catch {
    // WebGL not available
  }
  
  // 15. Push notification permission
  const pushPermission = (typeof Notification !== 'undefined' && Notification.permission) || 'default';
  
  // 16. Geolocation permission
  let geolocationPermission = 'denied';
  if ('geolocation' in navigator) {
    const permissions = navigator as Navigator & { permissions?: { query: (query: { name: string }) => Promise<{ state: string }> } };
    if (permissions.permissions) {
      permissions.permissions.query({ name: 'geolocation' }).then((result) => {
        geolocationPermission = result.state;
      }).catch(() => {
        geolocationPermission = 'prompt';
      });
    }
  }

  return {
    // Salon & Business context
    salonId,
    businessSlug,
    accountType: '',
    
    // Device & Browser
    isMobile,
    isTablet,
    isDesktop: !isMobile && !isTablet,
    userAgent,
    platform,
    
    // Screen info
    screenWidth,
    screenHeight,
    viewportWidth,
    viewportHeight,
    devicePixelRatio: window.devicePixelRatio || 1,
    
    // Connection
    connectionType,
    connectionDownlink,
    connectionSaveData: (connection as Record<string, unknown>)?.saveData || false,
    
    // Locale & Language
    language,
    languages,
    timezone,
    
    // UTM Attribution
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    hasUtmParams: !!(utmSource || utmMedium || utmCampaign),
    
    // Page & Navigation
    referrer,
    currentUrl,
    pagePath,
    pageHash,
    referrerDomain: referrer ? new URL(referrer).hostname : '',
    
    // Session tracking
    sessionId,
    bookingStep,
    selectedServicesSession: selectedServicesSession.split(',').filter(Boolean),
    bookingStartTime,
    sessionDurationSeconds: bookingStartTime ? Math.floor((Date.now() - new Date(bookingStartTime).getTime()) / 1000) : 0,
    
    // User journey
    firstVisit,
    visitCount,
    isReturning: visitCount > 1,
    
    // Performance
    pageLoadTime,
    
    // Interaction capabilities
    touchEnabled,
    maxTouchPoints,
    
    // Privacy
    dnt,
    cookiesEnabled,
    
    // Graphics
    webglRenderer,
    webglVendor,
    
    // Features
    pushPermission,
    geolocationPermission,
    
    // Meta/Facebook specific
    fbp: getCookie('_fbp'),
    fbc: MarketingCookies.getFbc() || getCookie('_fbc') || undefined,
    
    // Timestamps
    eventTimestamp: new Date().toISOString(),
    localTime: new Date().toLocaleString(),
    utcOffset: new Date().getTimezoneOffset(),
    
    // Misc
    isServer: false,
    timestamp: Date.now(),
  };
}

/**
 * Update session data for journey tracking
 */
export function updateSessionData(key: string, value: string) {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(key, value);
  }
}

/**
 * Track booking journey step
 */
export function trackBookingStep(step: string) {
  updateSessionData('booking_step', step);
  
  if (!sessionStorage.getItem('booking_start_time')) {
    updateSessionData('booking_start_time', new Date().toISOString());
  }
}

/**
 * Get browser fingerprint data
 */
export function getBrowserFingerprint() {
  if (typeof window === 'undefined') return {};
  
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cores: navigator.hardwareConcurrency || 0,
    memory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0,
    screenColorDepth: window.screen.colorDepth || 0,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    touchPoints: navigator.maxTouchPoints || 0,
    canvasSupported: typeof document.createElement('canvas').getContext('2d') !== 'undefined',
    webglSupported: (() => {
      try {
        return !!document.createElement('canvas').getContext('webgl');
      } catch {
        return false;
      }
    })(),
  };
}
